import { useState } from "react";
import { Usb } from "lucide-react";
import { toast } from "sonner";

import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useDeployPeripheralScripts,
  useScriptMdmCredentials,
} from "@/hooks/use-se-triggers";
import { ConfirmButton } from "./confirm-button";
import { ToolCard } from "./tool-card";

export function PeripheralScanTool({ tenantId }: { tenantId: string }) {
  const { data: credentials, isLoading, isError } = useScriptMdmCredentials(tenantId);
  const deploy = useDeployPeripheralScripts(tenantId);
  const [credentialId, setCredentialId] = useState("");
  const credential = credentials?.find((c) => c.id === credentialId);

  const onDeploy = () => {
    deploy.mutate(credentialId, {
      onSuccess: ({ outcome }) => {
        if (outcome === "completed") {
          toast.success("Scan scripts pushed to the MDM");
        } else {
          toast.info(
            "Still pushing after 30s. The MDM sync continues in the background; check device-management logs to confirm.",
          );
        }
      },
      onError: () => {
        toast.error("Failed to push the scan scripts");
      },
    });
  };

  return (
    <ToolCard
      icon={Usb}
      title="Scan peripheral devices"
      description="Pushes the tenant's custom scripts, including the USB, Bluetooth and display scans, to its MDM (JAMF, JumpCloud, Intune or Kandji). Devices report their peripherals on their next check-in."
    >
      {isError ? (
        <p className="text-destructive text-sm">Failed to load the tenant&apos;s MDM integrations.</p>
      ) : !isLoading && credentials?.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          This tenant has no JAMF, JumpCloud, Intune or Kandji integration.
        </p>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label>MDM integration</Label>
            <Select value={credentialId} onValueChange={setCredentialId} disabled={isLoading}>
              <SelectTrigger className="w-[300px]">
                <SelectValue placeholder={isLoading ? "Loading..." : "Select an MDM"} />
              </SelectTrigger>
              <SelectContent>
                {credentials?.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.label || c.name} · {c.provider}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <ConfirmButton
            label="Push scan scripts"
            title={`Push scripts to ${credential?.label || credential?.name || "the MDM"}?`}
            description="This writes to the customer's MDM: it creates or updates Harmony's custom scripts there, and they run on the targeted devices."
            confirmLabel="Push scripts"
            pending={deploy.isPending}
            disabled={!credential}
            onConfirm={onDeploy}
          />
        </div>
      )}
    </ToolCard>
  );
}
